/**
 * Cart recommendations for the Debutify theme.
 *
 * The products are rendered in Liquid (snippets/cart-recommendations.liquid).
 * This file only handles the carousel arrows, the variant picker and the add button.
 *
 * The cart drawer and cart page are rebuilt by theme.ajaxCart after every cart change.
 * Because this is a custom element, it sets itself up again every time it is inserted,
 * and it remembers which product the shopper was looking at.
 */
(function () {
  'use strict';

  if (!window.customElements || window.customElements.get('cart-recommendations')) return;

  /** Card index per position ('cart_page' / 'cart_drawer'), kept across re-renders. */
  var savedIndex = {};

  /**
   * Refreshes the cart after a product was added.
   * theme.ajaxCart.update() is the same call Debutify makes after its own add to cart:
   * it fetches /cart.js, rebuilds the drawer (cart.ajax) and the cart page (cart.ajax-page),
   * and updates the cart count, totals and savings.
   */
  function refreshCart() {
    var ajaxCart = window.theme && window.theme.ajaxCart;

    if (ajaxCart && typeof ajaxCart.update === 'function') {
      ajaxCart.update();
      return;
    }

    // Fallback, in case the theme's ajax cart is not available on this page.
    window.location.reload();
  }

  /** Debutify's spinner on buttons, if available. */
  function setLoading(button, isLoading) {
    var loadingState = window.theme && window.theme.loadingState;
    if (!loadingState) return;

    if (isLoading && typeof loadingState.init === 'function') loadingState.init(button);
    if (!isLoading && typeof loadingState.destroy === 'function') loadingState.destroy(button);
  }

  class CartRecommendations extends HTMLElement {
    constructor() {
      super();
      this.handleClick = this.handleClick.bind(this);
      this.handleChange = this.handleChange.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', this.handleClick);
      this.addEventListener('change', this.handleChange);

      this.track = this.querySelector('.cart-recs__track');
      if (this.track) this.track.addEventListener('scroll', this.handleScroll, { passive: true });

      this.restorePosition();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('change', this.handleChange);
      if (this.track) this.track.removeEventListener('scroll', this.handleScroll);
    }

    get key() {
      return this.dataset.position || 'cart';
    }

    get count() {
      return this.track ? this.track.children.length : 0;
    }

    restorePosition() {
      var track = this.track;
      var index = Math.min(savedIndex[this.key] || 0, Math.max(this.count - 1, 0));
      savedIndex[this.key] = index;
      if (!track || !index) return;

      requestAnimationFrame(function () {
        if (track.clientWidth) track.scrollLeft = index * track.clientWidth;
      });
    }

    handleScroll() {
      var track = this.track;
      if (!track || !track.clientWidth) return;
      savedIndex[this.key] = Math.round(track.scrollLeft / track.clientWidth);
    }

    handleClick(event) {
      var target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      var arrow = target.closest('.cart-recs__arrow');
      if (arrow) {
        event.preventDefault();
        event.stopPropagation();
        this.slide(arrow.dataset.direction === 'prev' ? -1 : 1);
        return;
      }

      var button = target.closest('.cart-recs__add');
      if (button) {
        // These buttons sit inside the cart form, make sure nothing submits it.
        event.preventDefault();
        event.stopPropagation();
        this.add(button);
      }
    }

    handleChange(event) {
      var select = event.target;
      if (!(select instanceof HTMLSelectElement) || !select.classList.contains('cart-recs__select')) return;

      // Keep the theme's cart form listeners from reacting to our variant picker.
      event.stopPropagation();

      var card = select.closest('.cart-recs__card');
      var option = select.options[select.selectedIndex];
      if (!card || !option) return;

      card.dataset.variantId = option.value;

      // Price follows the chosen variant (values are formatted by Liquid's money filter).
      var price = card.querySelector('.cart-recs__price');
      if (price) {
        var html = '';
        if (option.dataset.compare) {
          html += '<s class="cart-recs__price-compare"><span class="money">' + option.dataset.compare + '</span></s>';
        }
        html += '<span class="cart-recs__price-current"><span class="money">' + option.dataset.price + '</span></span>';
        price.innerHTML = html;
      }

      // Image follows the chosen variant, falls back to the product image.
      var image = card.querySelector('.cart-recs__image');
      var src = option.dataset.image || card.dataset.defaultImage;
      if (image && src) image.src = src;

      var error = card.querySelector('.cart-recs__error');
      if (error) error.hidden = true;
    }

    slide(step) {
      var track = this.track;
      var count = this.count;
      if (!track || !track.clientWidth || count < 2) return;

      var current = Math.round(track.scrollLeft / track.clientWidth);
      var next = (current + step + count) % count;
      var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      savedIndex[this.key] = next;
      track.scrollTo({ left: next * track.clientWidth, behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    async add(button) {
      var card = button.closest('.cart-recs__card');
      if (!card || button.disabled) return;

      var variantId = Number(card.dataset.variantId);
      var error = card.querySelector('.cart-recs__error');

      setLoading(button, true);
      button.disabled = true;
      card.classList.add('cart-recs__card--adding');
      if (error) error.hidden = true;

      try {
        var response = await fetch((this.dataset.addUrl || '/cart/add') + '.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
        });
        var data = await response.json();

        if (!response.ok || data.status) {
          throw new Error(typeof data.description === 'string' ? data.description : data.message);
        }

        // Rebuilds the drawer / cart page. The new markup no longer contains this product,
        // so the next recommendation takes its place.
        refreshCart();
      } catch (err) {
        // The cart may have re-rendered in the meantime, only touch the card if it is still there.
        if (!card.isConnected) return;

        setLoading(button, false);
        button.disabled = false;
        card.classList.remove('cart-recs__card--adding');

        if (error) {
          error.textContent = (err instanceof Error && err.message) || 'Could not add this product. Try again.';
          error.hidden = false;
        }
      }
    }
  }

  window.customElements.define('cart-recommendations', CartRecommendations);
})();